import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";

const order = ["spring", "summer", "autumn", "winter"];
const accents = {
  spring: "#B06A6A",
  summer: "#244B36",
  autumn: "#C88A2B",
  winter: "#397D94",
};

export default function Seasons() {
  const { t, lang } = useLang();
  const site = useSite();
  const seasons = site?.seasons || {};
  const [excursions, setExcursions] = useState([]);

  useEffect(() => {
    api.get("/excursions").then((r) => setExcursions(r.data)).catch(() => setExcursions([]));
  }, []);

  const count = (season) => excursions.filter((e) => (e.seasons || []).includes(season)).length;
  const hero = seasons?.summer?.hero_photo_id ? photoUrl(seasons.summer.hero_photo_id) : "/photos/photo_confluence.jpg";

  return (
    <div data-testid="seasons-page" style={{ background: "#F4F0E7", minHeight: "100vh" }}>
      <Seo
        path="/seasons"
        lang={lang}
        title={lang === "ru" ? "Сезоны на Алтае" : "Seasons in Altai"}
        description={lang === "ru"
          ? "Весна, лето, осень и зима на Алтае — выберите сезон и подходящие экскурсии АРГАЛИ."
          : "Spring, summer, autumn and winter in Altai — choose a season and discover suitable АРГАЛИ excursions."}
        image={hero}
      />

      <section className="relative grain overflow-hidden" style={{ minHeight: "68svh" }}>
        <img src={hero} alt={lang === "ru" ? "Сезоны Алтая" : "Seasons of Altai"} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,15,0.45), rgba(17,17,15,0.86))" }} />
        <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 pt-40 pb-16 flex flex-col justify-end" style={{ minHeight: "68svh" }}>
          <Reveal>
            <p className="eyebrow mb-4">{t("nav_seasons")}</p>
            <h1 className="display display-xl text-argali-ivory">
              {lang === "ru" ? (
                <>Четыре сезона.<br />Один Алтай.</>
              ) : (
                <>Four seasons.<br />One Altai.</>
              )}
            </h1>
            <p className="text-argali-ivory/80 mt-6 max-w-xl text-base md:text-lg leading-relaxed">
              {lang === "ru"
                ? "Каждое время года открывает другой Алтай. Выберите сезон — мы покажем маршруты, которые подходят именно для него."
                : "Every season reveals a different Altai. Choose a season and explore the routes that suit it best."}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 md:px-10 py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <p className="eyebrow" style={{ color: "#A96818" }}>{t("nav_seasons")}</p>
            <h2 className="display display-lg text-argali-obsidian mt-3">
              {lang === "ru" ? "Выберите время" : "Choose your season"}<br />
              <span className="text-argali-bronze">{lang === "ru" ? "для путешествия." : "for the journey."}</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            {order.map((season, index) => {
              const sp = seasons[season] || {};
              const seasonName = lang === "ru" ? sp.name_ru : sp.name_en;
              const tagline = lang === "ru" ? sp.tagline_ru : sp.tagline_en;
              const image = sp.hero_photo_id ? photoUrl(sp.hero_photo_id) : hero;

              return (
                <Reveal key={season} delay={index * 0.08}>
                  <Link
                    to={`/seasons/${season}`}
                    data-testid={`season-overview-${season}`}
                    className="group relative block overflow-hidden aspect-[16/10]"
                  >
                    <img
                      src={image}
                      alt={seasonName || season}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,15,0.12), rgba(17,17,15,0.84))" }} />
                    <div className="absolute top-5 left-5 w-12 h-[3px]" style={{ background: accents[season] }} />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <div className="flex items-end justify-between gap-5">
                        <h3 className="display display-md text-argali-ivory">{seasonName || season}</h3>
                        <span className="text-argali-sandstone text-sm whitespace-nowrap mb-2">
                          {count(season)} {t("routes_count")}
                        </span>
                      </div>
                      <p className="text-argali-ivory/75 text-sm mt-3 max-w-md">{tagline}</p>
                      <span className="inline-flex items-center gap-2 mt-5 text-[0.68rem] uppercase tracking-[0.16em] text-argali-gold">
                        {t("cta_see_routes")} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
